import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Menu,
  MenuItem,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import VolumeOffOutlinedIcon from '@mui/icons-material/VolumeOffOutlined';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import TerminalIcon from '@mui/icons-material/Terminal';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { isSoundEnabled, setSoundEnabled, getSoundProfile, setSoundProfile, SOUND_PROFILES, playSwitchSound, playClickSound } from '../../utils/soundEffects';

const navItems = [
  { label: 'About', path: '/about', index: '01' },
  { label: 'Work', path: '/work', index: '02' },
  { label: 'Writing', path: '/writing', index: '03' },
  { label: 'Reading', path: '/reading', index: '04' },
  { label: 'Codeforces', path: '/codeforces', index: 'CF', badge: '2013' },
  { label: 'Learning', path: '/learning', index: '05' },
  { label: 'Journey', path: '/journey', index: '06' },
  { label: 'Resume', path: '/resume', index: '07' },
  { label: 'Contact', path: '/contact', index: '08' }
];

const Navbar = ({ mode, toggleTheme, onOpenTerminal, onOpenPalette, onOpenRecruiter }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [soundActive, setSoundActive] = useState(false);
  const [activeProfile, setActiveProfile] = useState('topre');
  const [soundAnchorEl, setSoundAnchorEl] = useState(null);
  const theme = useTheme();
  const location = useLocation();

  useEffect(() => {
    setSoundActive(isSoundEnabled());
    setActiveProfile(getSoundProfile());
  }, []);

  const handleDrawerToggle = () => {
    playClickSound();
    setMobileOpen(!mobileOpen);
  };

  const handleToggleTheme = () => {
    playSwitchSound();
    toggleTheme();
  };

  const handleOpenSoundMenu = (e) => {
    setSoundAnchorEl(e.currentTarget);
  };

  const handleCloseSoundMenu = () => {
    setSoundAnchorEl(null);
  };

  const handleSelectProfile = (profileId) => {
    setSoundProfile(profileId);
    setActiveProfile(profileId);
    if (!soundActive) {
      setSoundActive(true);
      setSoundEnabled(true);
    }
    setTimeout(() => {
      playSwitchSound();
    }, 50);
  };

  const handleToggleSound = () => {
    const nextState = !soundActive;
    setSoundActive(nextState);
    setSoundEnabled(nextState);
    if (nextState) {
      playSwitchSound();
    }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(8, 10, 15, 0.82)' : 'rgba(251, 249, 245, 0.82)',
        backdropFilter: 'blur(16px) saturate(180%)',
        borderBottom: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        zIndex: theme.zIndex.drawer + 1,
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: { xs: '100%', sm: '100%', md: '1440px', lg: '1560px', xl: '1680px' },
          px: { xs: 2, sm: 3, md: 4, lg: 5 },
        }}
      >
        <Toolbar disableGutters sx={{ justifyContent: 'space-between', minHeight: { xs: 60, md: 70 } }}>
          {/* Masthead Logo / Identity */}
          <Box
            component={Link}
            to="/"
            onClick={playClickSound}
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'baseline',
              gap: 1.2,
              '&:hover .brand-name': {
                color: theme.palette.secondary.main,
              },
            }}
          >
            <Typography
              className="brand-name"
              variant="h6"
              sx={{
                fontFamily: '"Newsreader", "Playfair Display", Georgia, serif',
                fontWeight: 700,
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                letterSpacing: '-0.01em',
                transition: 'color 150ms ease',
              }}
            >
              Girish Kumar Goyal
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.68rem',
                letterSpacing: '0.08em',
                color: theme.palette.secondary.main,
                display: { xs: 'none', sm: 'inline-block' },
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {'// Archive'}
            </Typography>
          </Box>

          {/* Desktop Navigation Links */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Button
                  key={item.label}
                  component={NavLink}
                  to={item.path}
                  onClick={playClickSound}
                  sx={{
                    color: isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                    fontWeight: isActive ? 600 : 400,
                    fontSize: { md: '0.78rem', lg: '0.84rem' },
                    letterSpacing: '0.02em',
                    px: { md: 0.85, lg: 1.3 },
                    py: 0.6,
                    position: 'relative',
                    borderRadius: '6px',
                    '&:hover': {
                      color: theme.palette.text.primary,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
                    },
                    '&::after': isActive ? {
                      content: '""',
                      position: 'absolute',
                      bottom: '2px',
                      left: '10px',
                      right: '10px',
                      height: '2px',
                      borderRadius: '2px',
                      backgroundColor: theme.palette.secondary.main,
                    } : {},
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.64rem',
                      color: isActive ? theme.palette.secondary.main : theme.palette.text.secondary,
                      mr: 0.5,
                      opacity: isActive ? 1 : 0.6,
                    }}
                  >
                    {item.index}
                  </Box>
                  {item.label}
                  {item.badge && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.6,
                        px: 0.55,
                        py: 0.1,
                        borderRadius: '4px',
                        fontSize: '0.62rem',
                        fontFamily: '"JetBrains Mono", monospace',
                        fontWeight: 800,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(192, 132, 252, 0.2)' : 'rgba(124, 58, 237, 0.15)',
                        color: theme.palette.mode === 'dark' ? '#c084fc' : '#7c3aed',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(192, 132, 252, 0.4)' : 'rgba(124, 58, 237, 0.3)'}`,
                      }}
                    >
                      {item.badge}
                    </Box>
                  )}
                </Button>
              );
            })}

            {/* Recruiter Mode Button */}
            <Tooltip title="Quick 30-Second Executive Summary for Recruiters">
              <Button
                onClick={() => {
                  playClickSound();
                  if (onOpenRecruiter) onOpenRecruiter();
                }}
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<FlashOnIcon sx={{ fontSize: 14 }} />}
                sx={{
                  ml: 0.8,
                  borderRadius: '8px',
                  px: 1.3,
                  py: 0.45,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  textTransform: 'none',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 0 14px rgba(229, 169, 60, 0.35)'
                    : '0 0 10px rgba(184, 134, 11, 0.25)',
                }}
              >
                Recruiter Mode
              </Button>
            </Tooltip>

            {/* Command Palette / Search Trigger Button */}
            <Tooltip title="Open Command Palette (⌘K / Ctrl+K / /)">
              <Button
                onClick={() => {
                  playClickSound();
                  if (onOpenPalette) onOpenPalette();
                }}
                size="small"
                variant="outlined"
                startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ml: 0.5,
                  color: theme.palette.text.secondary,
                  borderColor: theme.palette.divider,
                  borderRadius: '8px',
                  px: 1.1,
                  py: 0.45,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.72rem',
                  textTransform: 'none',
                  '&:hover': {
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.secondary.main,
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' }, mr: 0.8 }}>Search</Box>
                <Box
                  component="span"
                  sx={{
                    px: 0.6,
                    py: 0.1,
                    borderRadius: '4px',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                  }}
                >
                  ⌘K
                </Box>
              </Button>
            </Tooltip>

            {/* Audio Effects Toggle & Profile Switcher */}
            <Tooltip title={soundActive ? `Audio: ${SOUND_PROFILES.find(p => p.id === activeProfile)?.name || 'ON'} (Click for switch profiles)` : 'Sound Effects: MUTED (Click to configure)'}>
              <IconButton
                onClick={handleOpenSoundMenu}
                aria-label="Toggle mechanical audio effects"
                sx={{
                  ml: 0.5,
                  color: soundActive ? theme.palette.secondary.main : theme.palette.text.secondary,
                  border: `1px solid ${soundActive ? theme.palette.secondary.main : theme.palette.divider}`,
                  borderRadius: '8px',
                  p: 0.7,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.secondary.main,
                  },
                }}
              >
                {soundActive ? (
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 16, px: '1px' }}>
                    <Box sx={{ width: 3, height: '100%', backgroundColor: theme.palette.secondary.main, borderRadius: '1px', animation: 'eqBar1 0.8s ease-in-out infinite' }} />
                    <Box sx={{ width: 3, height: '60%', backgroundColor: theme.palette.secondary.main, borderRadius: '1px', animation: 'eqBar2 0.8s ease-in-out infinite 0.2s' }} />
                    <Box sx={{ width: 3, height: '80%', backgroundColor: theme.palette.secondary.main, borderRadius: '1px', animation: 'eqBar3 0.8s ease-in-out infinite 0.4s' }} />
                  </Box>
                ) : (
                  <VolumeOffOutlinedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>

            {/* Soundboard Profile Menu */}
            <Menu
              anchorEl={soundAnchorEl}
              open={Boolean(soundAnchorEl)}
              onClose={handleCloseSoundMenu}
              PaperProps={{
                sx: {
                  width: 290,
                  p: 1,
                  borderRadius: '16px',
                  mt: 1.5,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(16px)',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.palette.mode === 'dark' ? '0 16px 40px rgba(0,0,0,0.6)' : '0 10px 30px rgba(0,0,0,0.1)',
                }
              }}
            >
              <Box sx={{ px: 1.5, py: 0.8, borderBottom: `1px solid ${theme.palette.divider}`, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', fontWeight: 800, color: theme.palette.secondary.main, letterSpacing: '0.08em' }}>
                  MECHANICAL AUDIO
                </Typography>
                <Button
                  size="small"
                  onClick={handleToggleSound}
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    px: 1,
                    py: 0.2,
                    color: soundActive ? '#ef4444' : '#10b981',
                    textTransform: 'none'
                  }}
                >
                  {soundActive ? 'Mute All' : 'Enable Audio'}
                </Button>
              </Box>

              {SOUND_PROFILES.map((prof) => {
                const isSelected = soundActive && activeProfile === prof.id;
                return (
                  <MenuItem
                    key={prof.id}
                    onClick={() => {
                      handleSelectProfile(prof.id);
                    }}
                    sx={{
                      borderRadius: '10px',
                      my: 0.4,
                      p: 1.2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      backgroundColor: isSelected ? (theme.palette.mode === 'dark' ? 'rgba(229, 169, 60, 0.12)' : 'rgba(184, 134, 11, 0.08)') : 'transparent',
                      border: isSelected ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '0.84rem', fontWeight: isSelected ? 800 : 600, color: isSelected ? theme.palette.secondary.main : theme.palette.text.primary }}>
                        {prof.name}
                      </Typography>
                      {isSelected && (
                        <Box component="span" sx={{ fontSize: '0.7rem', color: theme.palette.secondary.main, fontWeight: 800 }}>
                          ✓ Active
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.secondary, mt: 0.3 }}>
                      {prof.desc}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Menu>

            {/* Theme Toggle Button */}
            <Tooltip title="Toggle Theme">
              <IconButton
                onClick={handleToggleTheme}
                aria-label="Toggle dark and light mode"
                sx={{
                  ml: 0.5,
                  color: theme.palette.text.secondary,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  p: 0.7,
                  '&:hover': {
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.secondary.main,
                  },
                }}
              >
                {mode === 'dark' ? <LightModeOutlinedIcon sx={{ fontSize: 18 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Mobile Actions */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.8 }}>
            <IconButton
              onClick={() => {
                playClickSound();
                if (onOpenPalette) onOpenPalette();
              }}
              aria-label="Search catalog"
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                p: 0.7,
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <IconButton
              onClick={handleToggleSound}
              aria-label="Toggle sound"
              size="small"
              sx={{
                color: soundActive ? theme.palette.secondary.main : theme.palette.text.secondary,
                border: `1px solid ${soundActive ? theme.palette.secondary.main : theme.palette.divider}`,
                borderRadius: '8px',
                p: 0.7,
              }}
            >
              {soundActive ? <VolumeUpOutlinedIcon sx={{ fontSize: 18 }} /> : <VolumeOffOutlinedIcon sx={{ fontSize: 18 }} />}
            </IconButton>

            <IconButton
              onClick={handleToggleTheme}
              aria-label="Toggle dark and light mode"
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                p: 0.7,
              }}
            >
              {mode === 'dark' ? <LightModeOutlinedIcon sx={{ fontSize: 18 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />}
            </IconButton>

            <IconButton
              color="inherit"
              aria-label="Open archive menu"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ ml: 0.2 }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Catalog Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 290,
            backgroundColor: theme.palette.background.paper,
            p: 3,
            borderLeft: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="overline" sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, color: theme.palette.secondary.main }}>
            INDEX // ARCHIVE
          </Typography>
          <IconButton onClick={handleDrawerToggle} aria-label="Close menu" size="small">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <List sx={{ p: 0 }}>
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              onClick={() => {
                handleDrawerToggle();
                if (onOpenRecruiter) onOpenRecruiter();
              }}
              sx={{
                borderRadius: '8px',
                py: 1,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(229, 169, 60, 0.15)' : 'rgba(217, 119, 6, 0.12)',
                border: `1px solid ${theme.palette.secondary.main}`,
              }}
            >
              <FlashOnIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 1.5 }} />
              <ListItemText
                primary="Recruiter 30s Briefing"
                primaryTypographyProps={{ fontSize: '0.94rem', fontWeight: 800, color: theme.palette.secondary.main }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={Link}
              to="/"
              onClick={handleDrawerToggle}
              selected={location.pathname === '/'}
              sx={{ borderRadius: '6px', py: 1 }}
            >
              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: theme.palette.secondary.main, mr: 1.5 }}>
                00
              </Typography>
              <ListItemText primary="Home / Overview" primaryTypographyProps={{ fontSize: '0.94rem' }} />
            </ListItemButton>
          </ListItem>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={handleDrawerToggle}
                selected={location.pathname.startsWith(item.path)}
                sx={{ borderRadius: '6px', py: 1 }}
              >
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', color: theme.palette.secondary.main, mr: 1.5 }}>
                  {item.index}
                </Typography>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{item.label}</span>
                      {item.badge && (
                        <Box
                          component="span"
                          sx={{
                            px: 0.7,
                            py: 0.1,
                            borderRadius: '4px',
                            fontSize: '0.65rem',
                            fontFamily: '"JetBrains Mono", monospace',
                            fontWeight: 800,
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(192, 132, 252, 0.2)' : 'rgba(124, 58, 237, 0.15)',
                            color: theme.palette.mode === 'dark' ? '#c084fc' : '#7c3aed',
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(192, 132, 252, 0.4)' : 'rgba(124, 58, 237, 0.3)'}`,
                          }}
                        >
                          {item.badge}
                        </Box>
                      )}
                    </Box>
                  }
                  primaryTypographyProps={{ fontSize: '0.94rem' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => {
                handleDrawerToggle();
                if (onOpenTerminal) onOpenTerminal();
              }}
              sx={{ borderRadius: '6px', py: 1 }}
            >
              <TerminalIcon sx={{ fontSize: 16, color: theme.palette.secondary.main, mr: 1.5 }} />
              <ListItemText primary="Launch Unix Shell" primaryTypographyProps={{ fontSize: '0.94rem', fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
